import {useEffect, useRef, useState} from 'react';

import axios, {CancelTokenSource} from 'axios';

import {fetchPagedStaffList} from 'api/profile/staff';
import {StaffListRawRow} from 'types/profile';

export function useEmployeeLookup(enabled: boolean) {
    const [search, setSearch] = useState('');
    const [items, setItems] = useState<StaffListRawRow[]>([]);
    const [loading, setLoading] = useState(false);
    const [hasMore, setHasMore] = useState(true);
    const [error, setError] = useState(false);
    const request = useRef<CancelTokenSource | null>(null);
    const records = useRef(new Map<number, StaffListRawRow>());
    const page = useRef(0);
    const more = useRef(true);

    const cancel = () => {
        request.current?.cancel();
        request.current = null;
    };
    const loadMore = async () => {
        if (!enabled || request.current || !more.current) return;
        const source = axios.CancelToken.source();
        request.current = source;
        setLoading(true);
        setError(false);
        const term = search.trim();
        try {
            const response = await fetchPagedStaffList(
                {
                    paging: {pageNumber: page.current, pageSize: 50},
                    filters: term
                        ? [{id: term.includes('@') ? 'email' : 'fullName', value: term}]
                        : [],
                    sortings: [{id: 'fullName', desc: false}],
                },
                source.token,
            );
            if (source.token.reason) return;
            response.content.forEach((employee) => records.current.set(employee.id, employee));
            setItems((previous) =>
                Array.from(
                    new Map(
                        [...previous, ...response.content].map((employee) => [
                            employee.id,
                            employee,
                        ]),
                    ).values(),
                ),
            );
            page.current += 1;
            more.current = page.current < response.totalPages;
            setHasMore(more.current);
        } catch {
            if (!source.token.reason) setError(true);
        } finally {
            if (request.current === source) {
                request.current = null;
                setLoading(false);
            }
        }
    };
    useEffect(() => {
        cancel();
        page.current = 0;
        more.current = true;
        setItems([]);
        setHasMore(true);
        setError(false);
        setLoading(enabled);
        if (!enabled) return;
        const timer = setTimeout(loadMore, 300);
        return () => {
            clearTimeout(timer);
            cancel();
        };
    }, [enabled, search]);
    return {
        search,
        setSearch,
        items,
        loading,
        hasMore,
        error,
        loadMore,
        getEmployee: (id: number) => records.current.get(id),
    };
}
